/**
 * 작성 중인 폼 내용을 브라우저에 자동 저장하고, 다시 열었을 때 복구를 제안한다.
 *
 * 왜 필요한가: 일정/메모 등록 폼은 리치 에디터에 길게 쓰는 경우가 많은데,
 * 실수로 닫기·탭 종료·브라우저 크래시·로그아웃이 나면 전부 날아갔다. 저장되지
 * 않은 글은 되돌릴 방법이 아예 없어서 "다시 쓰기" 가 유일한 복구책이었다.
 *
 * 설계 결정:
 *  - **자동 적용하지 않는다.** 초안을 몰래 되살리면 새로 쓰려고 열었을 때 남의
 *    옛 내용이 들어차 있는 것처럼 보인다. 배너로 알리고 사용자가 고르게 한다.
 *  - **빈 폼은 저장하지 않는다.** 그러지 않으면 폼을 열었다 닫는 것만으로
 *    기존 초안이 빈 값으로 덮여 사라진다.
 *  - **종료 직전에 flush 한다.** 디바운스만 두면 마지막 몇 백 ms 의 입력이
 *    날아가는데, 정작 잃기 싫은 게 그 마지막 문장이다. pagehide /
 *    visibilitychange(hidden) 에서 동기 저장한다.
 *  - **만료가 있다.** 몇 달 전 초안을 복구 제안하는 건 도움이 아니라 소음이다.
 *
 * localStorage 는 브라우저·프로필 단위라 다른 PC 로는 넘어가지 않는다. 그건
 * 의도된 범위다 (서버 초안은 별도 논의 대상).
 */
import { useCallback, useEffect, useRef, useState } from 'react';

/** localStorage 키 접두어 — 다른 용도의 키와 섞이지 않게. */
export const DRAFT_KEY_PREFIX = 'sp:draft:';

/** 기본 디바운스. 타이핑 중 매 글자 저장은 낭비, 너무 길면 잃는 양이 커진다. */
export const DEFAULT_DRAFT_DEBOUNCE_MS = 700;

/** 기본 만료 — 7일. 그보다 오래된 초안은 복구 제안 대신 조용히 버린다. */
export const DEFAULT_DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** 저장 포맷. version 은 필드 구조가 바뀌었을 때 옛 초안을 버리기 위한 것. */
const DRAFT_VERSION = 1;

interface StoredDraft<T> {
  version: number;
  savedAt: number;
  value: T;
}

export interface RecoveredDraft<T> {
  value: T;
  /** epoch ms — "14:03 에 자동 저장됨" 같은 안내에 쓴다. */
  savedAt: number;
}

export interface UseFormDraftOptions<T> {
  /**
   * 초안을 구분하는 키. 폼 종류 + 대상까지 담아야 한다
   * (예: `schedule:new:site-7`, `schedule:edit:42`). 같은 키를 공유하면
   * 다른 대상의 초안이 서로를 덮는다.
   */
  key: string;
  /** 폼이 열려 있는 동안만 true. false 면 읽기/쓰기 모두 하지 않는다. */
  enabled: boolean;
  /** 이 값이 "아직 아무것도 안 쓴 상태" 인가. true 면 저장하지 않는다. */
  isEmpty: (value: T) => boolean;
  debounceMs?: number;
  maxAgeMs?: number;
}

export interface UseFormDraftResult<T> {
  /**
   * 복구 가능한 초안. 배너를 띄울 근거이며, `null` 이면 띄우지 않는다.
   * 사용자가 이어쓰기/버리기를 고르면 dismissRecovered 로 없앤다.
   */
  recovered: RecoveredDraft<T> | null;
  /** 마지막 자동 저장 시각(epoch ms). 아직 저장 전이면 null. */
  savedAt: number | null;
  /** 배너만 닫는다 — 저장된 초안은 그대로 남는다(이어쓰기 선택 시). */
  dismissRecovered: () => void;
  /** 초안을 버린다 — 배너도 닫힌다. 저장 성공 / 명시적 버리기에 사용. */
  clear: () => void;
}

/** localStorage 접근은 사파리 프라이빗 모드 등에서 throw 한다 — 전부 감싼다. */
function safeRead(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeWrite(key: string, raw: string): void {
  try {
    window.localStorage.setItem(key, raw);
  } catch {
    // 용량 초과(QuotaExceeded)/차단. 초안은 부가 기능이므로 조용히 포기한다.
  }
}

function safeRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // 무시 — 위와 같은 이유.
  }
}

/**
 * 저장된 초안을 읽는다. 형식이 깨졌거나 버전이 다르거나 만료됐으면 null 을
 * 돌려주고 해당 키를 지운다 — 못 쓰는 초안을 계속 들고 있을 이유가 없다.
 */
export function readDraft<T>(key: string, maxAgeMs: number): RecoveredDraft<T> | null {
  const storageKey = DRAFT_KEY_PREFIX + key;
  const raw = safeRead(storageKey);
  if (!raw) return null;

  let parsed: StoredDraft<T> | null = null;
  try {
    parsed = JSON.parse(raw) as StoredDraft<T>;
  } catch {
    safeRemove(storageKey);
    return null;
  }
  if (!parsed || parsed.version !== DRAFT_VERSION || typeof parsed.savedAt !== 'number') {
    safeRemove(storageKey);
    return null;
  }
  if (Date.now() - parsed.savedAt > maxAgeMs) {
    safeRemove(storageKey);
    return null;
  }
  return { value: parsed.value, savedAt: parsed.savedAt };
}

/** 초안 1건을 지운다 — 폼 밖(저장 성공 콜백 등)에서도 쓸 수 있게 export. */
export function clearDraft(key: string): void {
  safeRemove(DRAFT_KEY_PREFIX + key);
}

export function useFormDraft<T>(
  value: T,
  {
    key,
    enabled,
    isEmpty,
    debounceMs = DEFAULT_DRAFT_DEBOUNCE_MS,
    maxAgeMs = DEFAULT_DRAFT_MAX_AGE_MS,
  }: UseFormDraftOptions<T>,
): UseFormDraftResult<T> {
  const [recovered, setRecovered] = useState<RecoveredDraft<T> | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // 최신 value/isEmpty 를 effect 재실행 없이 읽기 위한 ref — value 가 바뀔 때마다
  // 리스너를 붙였다 떼면 타이핑 중 낭비가 크다.
  const valueRef = useRef(value);
  valueRef.current = value;
  const isEmptyRef = useRef(isEmpty);
  isEmptyRef.current = isEmpty;

  // 초안이 지워진 뒤(저장 성공 등)에는 남은 디바운스가 되살리지 못하게 막는다.
  const discardedRef = useRef(false);

  const write = useCallback(() => {
    if (discardedRef.current) return;
    const current = valueRef.current;
    // 빈 폼을 저장하면 열었다 닫기만으로 기존 초안이 사라진다.
    if (isEmptyRef.current(current)) return;
    const now = Date.now();
    safeWrite(
      DRAFT_KEY_PREFIX + key,
      JSON.stringify({ version: DRAFT_VERSION, savedAt: now, value: current } as StoredDraft<T>),
    );
    setSavedAt(now);
  }, [key]);

  // 열릴 때 한 번: 복구 가능한 초안이 있는지 확인.
  useEffect(() => {
    if (!enabled) {
      // 닫히면 배너/저장시각 상태를 리셋 — 다음에 열 때 새로 판단한다.
      setRecovered(null);
      setSavedAt(null);
      discardedRef.current = false;
      return;
    }
    setRecovered(readDraft<T>(key, maxAgeMs));
  }, [enabled, key, maxAgeMs]);

  // 값이 바뀌면 디바운스 저장.
  useEffect(() => {
    if (!enabled) return;
    const timer = window.setTimeout(write, debounceMs);
    return () => window.clearTimeout(timer);
  }, [enabled, value, debounceMs, write]);

  // 종료·백그라운드 전환 직전 동기 flush — 디바운스 대기 중이던 마지막 입력을
  // 살린다. beforeunload 는 모바일에서 안 불리는 경우가 있어 pagehide 를 쓴다.
  useEffect(() => {
    if (!enabled) return;
    const flush = () => write();
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') write();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, write]);

  const dismissRecovered = useCallback(() => setRecovered(null), []);

  const clear = useCallback(() => {
    discardedRef.current = true;
    clearDraft(key);
    setRecovered(null);
    setSavedAt(null);
  }, [key]);

  return { recovered, savedAt, dismissRecovered, clear };
}

export default useFormDraft;
